import { useQuery } from "@tanstack/react-query";
import { myAccountApi } from "./axios";
import { ValidateCurrentToken } from "../../helpers";

export const auth = () => {
  const t = ValidateCurrentToken();
  if (!t) throw new Error("TOKEN INVALIDO");
  return { headers: { Authorization: t } };
};

export const getMySubscription = async () => {
  const { data } = await myAccountApi.get("/subscription", auth());
  return data;
};

export const getAvailablePlans = async () => {
  const { data } = await myAccountApi.get("/plans", auth());
  return data;
};

// Estado del plan para la pantalla "Mi plan": consumo de comprobantes del
// período + features contratadas + cuotas (usuarios/sucursales/productos/storage).
export const getPlanUsage = async () => {
  const { data } = await myAccountApi.get("/plan-usage", auth());
  return data;
};

// Chequeo liviano para el POS: ¿puede emitir un comprobante más?
// { canEmit, remaining, unlimited, included, used, extra }
export const canEmitDocument = async () => {
  const { data } = await myAccountApi.get("/can-emit", auth());
  return data;
};

// Features del plan activo del cliente — para ocultar módulos del menú.
// Cacheado 5 min. Para SUPERADMIN (sin suscripción) devuelve hasSubscription=false
// y el menú NO se filtra.
export const usePlanFeatures = () => {
  const query = useQuery(
    ["plan-features"],
    async () => {
      try {
        const res = await getPlanUsage();
        const d = res?.data ?? res;
        return {
          hasSubscription: !!d?.hasSubscription,
          features: (d?.features ?? []) as string[],
        };
      } catch {
        return { hasSubscription: false, features: [] as string[] };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
  return {
    hasSubscription: query.data?.hasSubscription ?? false,
    features: query.data?.features ?? [],
    isLoading: query.isLoading,
  };
};

export const requestChange = async (payload: {
  tipo: string;
  planId?: number;
  mensaje?: string;
}) => {
  const { data } = await myAccountApi.post("/request", payload, auth());
  return data;
};

export const updateSriCredentials = async (payload: {
  userOpenSri: string;
  passwordOpenSri: string;
}) => {
  const { data } = await myAccountApi.put("/sri-credentials", payload, auth());
  return data;
};

// Número de Telegram autorizado a operar el bot de facturación de esta
// cuenta (pasar "" o null para eliminarlo), y si lo que arma el bot queda
// como Cotización pendiente de revisión (true) o crea y emite la factura
// directo (false).
export const updateBotPhone = async (
  botPhoneNumber: string | null,
  botAutoQuotation?: boolean,
) => {
  const { data } = await myAccountApi.put(
    "/bot-phone",
    { botPhoneNumber, botAutoQuotation },
    auth(),
  );
  return data;
};

export const updateMyLogo = async (logo: string) => {
  const { data } = await myAccountApi.put("/logo", { logo }, auth());
  return data;
};

export const deleteMyLogo = async () => {
  const { data } = await myAccountApi.delete("/logo", auth());
  return data;
};

export const getMyApiKeys = async () => {
  const { data } = await myAccountApi.get("/api-keys", auth());
  return data;
};

export const generateMyApiKey = async (nombre?: string) => {
  const { data } = await myAccountApi.post(
    "/api-keys/generate",
    { nombre },
    auth(),
  );
  return data;
};

export const revokeMyApiKey = async (id: number) => {
  const { data } = await myAccountApi.patch(
    `/api-keys/${id}/revoke`,
    {},
    auth(),
  );
  return data;
};
